import React, { useState, useMemo } from 'react';
import { Snowflake } from 'lucide-react';
import JanelaModal from '../../JanelaModal';
import { darEntradaDeEmbrioes } from '../../../data/estoque';
import { EMBRYO_STAGE_LABELS } from '../../../constants/workflowGranular';
import { MARK } from '../../../constants';

// Só as fases atuais: o objeto de rótulos carrega os apelidos antigos ('Bi',
// 'Be') para ler dado velho, e oferecê-los na entrada criaria dado velho novo.
const FASES = ['BI', 'BL', 'BX', 'BN', 'BX-ECLO'];

const input = 'w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

const Campo = ({ label, children, dica }) => (
  <div>
    <label className="block text-xs font-medium text-slate-700 mb-1">{label}</label>
    {children}
    {dica && <p className="text-xs text-slate-400 mt-0.5">{dica}</p>}
  </div>
);

/**
 * Entrada de embrião que chegou de fora — comprado, ou recebido de outro
 * laboratório.
 *
 * O SERVIÇO É OBRIGATÓRIO e o campo diz por quê. `embryos.service_id` é NOT
 * NULL, mas a razão não é a constraint: embrião é de alguém, e sem cliente
 * ninguém sabe a quem devolver a palheta. O tipo "Compra" existe no cadastro
 * exatamente para abrigar isto.
 */
const EntradaDeEmbrioes = ({ laboratoryId, clients = [], onPronto, onFechar }) => {
  const [f, setF] = useState({
    clientId: '', quantidade: 1, doadora: '', touro: '', stage: 'BX',
    metodo: 'Vitrificação', freeze_date: '', numeroInicial: '',
    botijao: '', caneca: '', rack: '', altura: '', observacao: '',
  });
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const opcoes = useMemo(
    () => [...clients].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [clients]);

  const pode = f.clientId && (parseInt(f.quantidade) || 0) > 0 && !salvando;

  const gravar = async () => {
    setSalvando(true); setErro('');
    try {
      const r = await darEntradaDeEmbrioes({ laboratoryId, ...f });
      onPronto?.(r);
      onFechar?.();
    } catch (e) {
      console.error('entrada de embriões:', e);
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <JanelaModal chave="entrada-embrioes" Icone={Snowflake} titulo="Entrada de embriões" onFechar={onFechar} largura="sm:max-w-2xl">
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <p className="text-xs text-slate-500">
            Para embrião que chegou de fora — comprado, ou recebido de outro laboratório.
            Ele pertence ao cliente, não a um serviço: não saiu de trabalho nenhum
            daqui. O que foi produzido aqui entra sozinho pela FIV.
          </p>

          <Campo label="Cliente *"
            dica="De quem é o embrião — é isso que diz a quem devolver a palheta.">
            <select className={input} value={f.clientId}
              onChange={(e) => set('clientId', e.target.value)}>
              <option value="">Escolher…</option>
              {opcoes.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </Campo>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Campo label="Quantidade *">
              <input className={input} type="number" min="1" value={f.quantidade}
                onChange={(e) => set('quantidade', e.target.value)} />
            </Campo>
            <Campo label="Fase">
              <select className={input} value={f.stage} onChange={(e) => set('stage', e.target.value)}>
                {FASES.map(v => <option key={v} value={v}>{EMBRYO_STAGE_LABELS[v]}</option>)}
              </select>
            </Campo>
            <Campo label="Método">
              <select className={input} value={f.metodo} onChange={(e) => set('metodo', e.target.value)}>
                <option value="Vitrificação">Vitrificação</option>
                <option value="Direct Transfer">Direct Transfer</option>
              </select>
            </Campo>
            <Campo label="Congelado em">
              <input className={input} type="date" value={f.freeze_date}
                onChange={(e) => set('freeze_date', e.target.value)} />
            </Campo>
            <Campo label="Nº na palheta" dica="Opcional — o da etiqueta de quem produziu">
              <input className={input} type="number" min="1" value={f.numeroInicial}
                onChange={(e) => set('numeroInicial', e.target.value)} />
            </Campo>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Campo label="Doadora" dica="Como veio na etiqueta">
              <input className={input} value={f.doadora}
                onChange={(e) => set('doadora', e.target.value)} />
            </Campo>
            <Campo label="Touro">
              <input className={input} value={f.touro}
                onChange={(e) => set('touro', e.target.value)} />
            </Campo>
          </div>

          <div>
            <p className="text-xs font-medium text-slate-700 mb-1">Onde está</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <input className={input} placeholder="Botijão" value={f.botijao}
                onChange={(e) => set('botijao', e.target.value)} />
              <input className={input} placeholder="Caneca" value={f.caneca}
                onChange={(e) => set('caneca', e.target.value)} />
              <input className={input} placeholder="Rack" value={f.rack}
                onChange={(e) => set('rack', e.target.value)} />
              <select className={input} value={f.altura} onChange={(e) => set('altura', e.target.value)}>
                <option value="">Altura…</option>
                <option value="cima">Cima</option>
                <option value="baixo">Baixo</option>
              </select>
            </div>
            <p className="text-xs text-slate-400 mt-1">
              Pode ficar em branco e ser preenchido depois, pela própria lista.
            </p>
          </div>

          <Campo label="Observação">
            <input className={input} value={f.observacao}
              onChange={(e) => set('observacao', e.target.value)} />
          </Campo>

          {/* O número da palheta é por serviço e sai do banco, não daqui — por
              isso a tela promete o intervalo sem mostrá-lo antes de gravar. */}
          {pode && (
            <p className="text-xs text-slate-500">
              {MARK.PENDING} Entram {f.quantidade} embriõ{f.quantidade > 1 ? 'es' : ''} como
              congelado{f.quantidade > 1 ? 's' : ''}
              {f.numeroInicial
                ? `, numerados de ${f.numeroInicial} a ${(parseInt(f.numeroInicial) || 0) + (parseInt(f.quantidade) || 1) - 1}.`
                : ', sem número — o da palheta pode ser informado depois.'}
            </p>
          )}

          {erro && (
            <div className="bg-slate-50 border border-slate-300 text-slate-700 text-sm rounded px-3 py-2">
              {erro}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onFechar}
            className="px-4 py-2 border border-slate-300 rounded text-sm text-slate-700 hover:bg-slate-50">
            Cancelar
          </button>
          <button onClick={gravar} disabled={!pode}
            className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-40">
            {salvando ? 'Gravando…' : 'Dar entrada'}
          </button>
        </div>
    </JanelaModal>
  );
};

export default EntradaDeEmbrioes;
